import {
  Box,
  Card,
  createStyles,
  Grid,
  Group,
  SimpleGrid,
  Skeleton,
  SkeletonProps,
  Stack,
} from '@mantine/core';
import React from 'react';

import { ReactComponent as SearchNormal } from '@portals/icons/linear/search-normal 2.svg';

import { ReactComponent as Placeholder } from '../../../assets/img/icons/sidebar/mock/placeholder.svg';
import { useBrandingContext } from '../../../pages/branding/branding.context';

const SkeletonWithoutAnimation = (props: SkeletonProps) => (
  <Skeleton animate={false} {...props} />
);

const TreeSkeleton = () => (
  <Grid.Col span={2}>
    <Stack
      p="md"
      pt={25}
      sx={(theme) => ({
        backgroundColor: theme.colors.gray[1],
        height: '100%',
        width: '100%',
      })}
    >
      <Group spacing="xs">
        <SkeletonWithoutAnimation circle height={9} />

        <SkeletonWithoutAnimation width={45} height={9} />
      </Group>

      <SkeletonWithoutAnimation ml={30} width={45} height={9} />

      <SkeletonWithoutAnimation ml={50} width={45} height={9} />
      <SkeletonWithoutAnimation ml={50} width={60} height={9} />
      <SkeletonWithoutAnimation ml={50} width={45} height={9} />
      <SkeletonWithoutAnimation ml={50} width={60} height={9} />

      <Group spacing="xs">
        <SkeletonWithoutAnimation circle height={9} />

        <SkeletonWithoutAnimation width={45} height={9} />
      </Group>

      <SkeletonWithoutAnimation ml={30} width={45} height={9} />

      <SkeletonWithoutAnimation ml={50} width={45} height={9} />
      <SkeletonWithoutAnimation ml={50} width={60} height={9} />
      <SkeletonWithoutAnimation ml={50} width={45} height={9} />
      <SkeletonWithoutAnimation ml={50} width={60} height={9} />
    </Stack>
  </Grid.Col>
);

const useStorePreviewStyles = createStyles(
  (theme, backgroundColor: string) => ({
    activeItem: {
      '&::after': {
        background: backgroundColor,
      },
    },
  })
);

function StoreSidebarSkeleton() {
  const { portalConfig, defaultValues } = useBrandingContext();
  const { classes } = useStorePreviewStyles(
    portalConfig.design?.store?.menu_items_text_color ||
      defaultValues.design.store.menu_items_text_color
  );

  return (
    <Grid.Col span={2}>
      <Stack p="md" pt={25} bg="white" w="100%" h="100%" spacing="xl">
        <Group
          w="100%"
          h={25}
          bg="gray.1"
          align="center"
          position="right"
          px="md"
          sx={(theme) => ({
            borderRadius: theme.radius.md,
            border: `1px solid ${theme.colors.gray[3]}`,
          })}
        >
          <SearchNormal width={12} height={12} />
        </Group>

        <Stack spacing="md">
          <Group align="center" position="apart" px="sm">
            <SkeletonWithoutAnimation width="30%" height={8} />

            <SkeletonWithoutAnimation width={14} height={9} color="red" />
          </Group>

          <Group
            align="center"
            position="apart"
            bg={
              portalConfig.design?.store?.menu_buttons_color ||
              defaultValues.design.store.menu_buttons_color
            }
            p="sm"
            sx={(theme) => ({ borderRadius: theme.radius.md })}
          >
            <SkeletonWithoutAnimation
              width="50%"
              height={8}
              className={classes.activeItem}
            />

            <SkeletonWithoutAnimation
              width={14}
              height={9}
              className={classes.activeItem}
            />
          </Group>

          <Group align="center" position="apart" px="sm">
            <SkeletonWithoutAnimation width="20%" height={8} />

            <SkeletonWithoutAnimation width={14} height={9} />
          </Group>

          <Group align="center" position="apart" px="sm">
            <SkeletonWithoutAnimation width="40%" height={8} />

            <SkeletonWithoutAnimation width={14} height={9} />
          </Group>

          <Group align="center" position="apart" px="sm">
            <SkeletonWithoutAnimation width="20%" height={8} />

            <SkeletonWithoutAnimation width={14} height={9} />
          </Group>
        </Stack>
      </Stack>
    </Grid.Col>
  );
}

const ContentSkeleton = () => (
  <Stack
    sx={(theme) => ({
      backgroundColor: theme.colors.gray[0],
      height: 'calc(100% - 120px)',
      flex: 1,
    })}
    p="md"
  >
    <Card
      shadow="sm"
      sx={() => ({
        height: 100,
        flexShrink: 0,
      })}
    >
      <Stack
        sx={() => ({
          height: '100%',
        })}
      >
        <Group>
          <SkeletonWithoutAnimation
            width={50}
            height={9}
            sx={(theme) => ({
              '&:after': {
                backgroundColor: theme.colors.gray[1],
              },
            })}
          />
          <SkeletonWithoutAnimation
            width={30}
            height={9}
            sx={(theme) => ({
              '&:after': {
                backgroundColor: theme.colors.gray[1],
              },
            })}
          />
        </Group>

        <Group grow>
          <SkeletonWithoutAnimation
            width="20%"
            height={50}
            sx={(theme) => ({
              '&:after': {
                backgroundColor: theme.colors.gray[1],
              },
            })}
          />
          <SkeletonWithoutAnimation
            width="20%"
            height={50}
            sx={(theme) => ({
              '&:after': {
                backgroundColor: theme.colors.gray[1],
              },
            })}
          />
          <SkeletonWithoutAnimation
            width="20%"
            height={50}
            sx={(theme) => ({
              '&:after': {
                backgroundColor: theme.colors.gray[1],
              },
            })}
          />
          <SkeletonWithoutAnimation
            width="20%"
            height={50}
            sx={(theme) => ({
              '&:after': {
                backgroundColor: theme.colors.gray[1],
              },
            })}
          />
          <SkeletonWithoutAnimation
            width="20%"
            height={50}
            sx={(theme) => ({
              '&:after': {
                backgroundColor: theme.colors.gray[1],
              },
            })}
          />
        </Group>
      </Stack>
    </Card>

    <Group grow sx={() => ({ height: '100%' })}>
      <Card
        shadow="sm"
        sx={() => ({
          height: '100%',
        })}
      >
        <Group>
          <SkeletonWithoutAnimation
            width={50}
            height={9}
            sx={(theme) => ({
              '&:after': {
                backgroundColor: theme.colors.gray[1],
              },
            })}
          />
          <SkeletonWithoutAnimation
            width={30}
            height={9}
            sx={(theme) => ({
              '&:after': {
                backgroundColor: theme.colors.gray[1],
              },
            })}
          />
        </Group>

        <Group
          py={10}
          sx={() => ({
            height: '100%',
          })}
        >
          <SkeletonWithoutAnimation
            width="100%"
            height="100%"
            sx={(theme) => ({
              '&:after': {
                backgroundColor: theme.colors.gray[1],
              },
            })}
          />
        </Group>
      </Card>

      <Card
        shadow="sm"
        sx={() => ({
          height: '100%',
        })}
      >
        <Group>
          <SkeletonWithoutAnimation
            width={50}
            height={9}
            sx={(theme) => ({
              '&:after': {
                backgroundColor: theme.colors.gray[1],
              },
            })}
          />
          <SkeletonWithoutAnimation
            width={30}
            height={9}
            sx={(theme) => ({
              '&:after': {
                backgroundColor: theme.colors.gray[1],
              },
            })}
          />
        </Group>

        <Group
          py={10}
          sx={() => ({
            height: '100%',
          })}
        >
          <SkeletonWithoutAnimation
            width="100%"
            height="100%"
            sx={(theme) => ({
              '&:after': {
                backgroundColor: theme.colors.gray[1],
              },
            })}
          />
        </Group>
      </Card>
    </Group>
  </Stack>
);

const StoreProductCardSkeleton = () => (
  <Card shadow="sm" h={350} p={0}>
    <Stack p={0} spacing={0} bg="gray.2" w="100%" h="100%">
      <Box pos="relative" h={234} w="100%">
        <SkeletonWithoutAnimation
          width={81}
          height={20}
          radius="lg"
          sx={(theme) => ({
            position: 'absolute',
            left: theme.spacing.md,
            top: theme.spacing.md,
            flexShrink: 0,

            '&:after': {
              backgroundColor: theme.colors.gray[5],
            },
          })}
        />
      </Box>

      <Stack bg="white" h={116} p="md">
        <SkeletonWithoutAnimation
          width="30%"
          height={14}
          radius="lg"
          sx={(theme) => ({
            '&:after': {
              backgroundColor: theme.colors.gray[3],
            },
          })}
        />

        <SkeletonWithoutAnimation
          width="90%"
          height={14}
          radius="lg"
          sx={(theme) => ({
            '&:after': {
              backgroundColor: theme.colors.gray[2],
            },
          })}
        />

        <SkeletonWithoutAnimation
          width="15%"
          height={14}
          radius="lg"
          sx={(theme) => ({
            '&:after': {
              backgroundColor: theme.colors.gray[4],
            },
          })}
        />
      </Stack>
    </Stack>
  </Card>
);

function StoreContentSkeleton() {
  return (
    <Stack
      sx={(theme) => ({
        backgroundColor: theme.colors.gray[0],
        height: 'calc(100% - 120px)',
        flex: 1,
      })}
      p="md"
    >
      <SimpleGrid cols={4}>
        <StoreProductCardSkeleton />
        <StoreProductCardSkeleton />
        <StoreProductCardSkeleton />
        <StoreProductCardSkeleton />
      </SimpleGrid>
    </Stack>
  );
}

export function PortalSkeleton() {
  return (
    <Stack
      className="content-preview"
      spacing={0}
      sx={{
        height: '100%',
      }}
    >
      <Grid
        gutter={0}
        columns={10}
        sx={{
          flex: 1,
        }}
      >
        <TreeSkeleton />

        <Grid.Col
          span={8}
          sx={() => ({
            height: '100%',
          })}
        >
          <Group
            sx={(theme) => ({
              height: 120,
              backgroundColor: theme.colors.gray[2],
            })}
            grow
          >
            <Placeholder />
          </Group>

          <ContentSkeleton />
        </Grid.Col>
      </Grid>
    </Stack>
  );
}

export function StoreSkeleton() {
  return (
    <Stack
      className="content-preview"
      spacing={0}
      sx={{
        height: '100%',
      }}
    >
      <Grid
        gutter={0}
        columns={10}
        sx={{
          flex: 1,
        }}
      >
        <StoreSidebarSkeleton />

        <Grid.Col
          span={8}
          sx={() => ({
            height: '100%',
          })}
        >
          <StoreContentSkeleton />
        </Grid.Col>
      </Grid>
    </Stack>
  );
}
